/** @jsx jsx */
import { useState, useCallback, memo } from 'react';
import { styled as styled2 } from '@compiled/react';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { Props } from './types.tsx';

const defaultIconSize = 16;

/* Represents an asynchronous icon loader that handles loading of icons from URLs with error handling.
 * It displays the icon fetched from the provided URL. In case of an error or if the URL is undefined,
 * a fallback UI component or placeholder is displayed.
 * This component accepts various props like URL, title, alt text, dimensions, and a fallback component
 * to customize the behavior and presentation of the loaded or placeholder icon.
 * The component also includes functionality to prevent the icon from being draggable.
 */
const AsyncIcon = memo(({ url, title, alt, width, height, fallback }: Props) => {
	const [isBroken, setIsBroken] = useState(false);

	const onError = useCallback(() => {
		setIsBroken(true);
	}, []);

	const convertSize = useCallback((size: number) => (size ? `${size}px` : '0'), []);

	if (isBroken && fallback) {
		return fallback();
	}

	const convertedWidth = convertSize(width ?? defaultIconSize);
	const convertedHeight = convertSize(height ?? defaultIconSize);

	if (url) {
		return (
			<Image
				src={url}
				width={convertedWidth}
				height={convertedHeight}
				title={title ?? undefined}
				alt={alt ?? ''}
				onError={onError}
				/**
				 * Disabling dragging so drags happen on the parent instead of this icon.
				 */
				draggable={false}
				data-vc="common-components-async-icon"
			/>
		);
	}

	return (
		<div
			css={placeholderStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ height: convertedHeight, width: convertedWidth }}
			data-testid="common-components-async-icon.placeholder"
		/>
	);
});

const placeholderStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	display: 'flex',
	flex: 'none',
	backgroundColor: token('color.background.input.hovered'),
});

// Firefox displays the alt text when the image can't load, the narrow provided width
// means this alt text leaks into the summary vertically if overflow is allowed
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled2.img({
	overflow: 'hidden',
	flex: 'none',
});

// Needed for snapshot tests to work correctly
AsyncIcon.displayName = 'AsyncIcon';

export default AsyncIcon;
